import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../../redux/store";
import {
  calculateDGCoinAmount,
  convertCurrencyAmount,
} from "../../utils/utils";
import { computeCartItemData } from "../../pages/Cart/utils";

export interface CartItem {
  id: string;
  planId: string | number;
  categoryId?: string | number;
  type: string;
  name: string;
  product: string;
  image: string;
  atm_price: number;
  wallet_price: number;
  price: number; // Final discounted price per unit
  originalPrice?: number; // Base subtotal price before discount
  discount?: number; // Direct discount amount per unit (or calculated)
  discountPercentage?: number; // e.g., 15 for 15%
  isFixed: boolean;
  bonusPoints?: number; // Points earned per item (e.g. 120pts)
  quantity: number;
  unique_number?: string;
  priceCurrencyId: number;
  additionalInputValue?: string;
  price_type: string;
  plan_discount: string | number;
  point_bonus: string | number;
  bonus_points: string | number;
  points: string | number;
}

interface CartState {
  userCarts: Record<string, CartItem[]>;
}

const initialState: CartState = {
  userCarts: {},
};

export const generateCartItemId = (item: {
  categoryId?: string | number;
  planId: string | number;
  type: string;
  unique_number?: string;
  additionalInputValue?: string;
}) => {
  const cat = String(item.categoryId ?? "0").trim();
  const plan = String(item.planId).trim();
  const type = (item.type || "").trim().toLowerCase();
  const recipient = item.unique_number?.trim().toLowerCase() || "none";
  const extra = item.additionalInputValue?.trim().toLowerCase() || "none";

  return `${cat}_${plan}_${type}_${recipient}_${extra}`;
};

export type CartPaymentMethod =
  | "COIN"
  | "POINT"
  | "TRANSFER"
  | "OPAY"
  | "BLUSALT"
  | "NOWPAY"
  | "ATM";

export interface CartCheckoutItem {
  type: string;
  plan_id: number;
  quantity: number;
  unique_number?: string;
  amount?: number;
  server?: string;
}

export interface CartCheckoutRequest {
  userId: string;
  payment_method: CartPaymentMethod;
  currency_id: number;
  source: "WEB";
  items: CartCheckoutItem[];
}

export interface CartCheckoutResponse {
  status: "success";
  message: string;
  data: {
    payment_url?: string;
    // bank transfer
    amount?: number;
    account_name?: string;
    account_number?: string;
    bank_name?: string;
    reference?: string | number;
    accountExpiry?: string;
  };
}

// Items the backend cannot take as-is: airtime/electric (and percent-priced
// plans) carry their amount in additionalInputValue, top-ups their server.
export const toCartCheckoutItem = (item: CartItem): CartCheckoutItem => {
  const extra = item.additionalInputValue?.trim() || undefined;
  const isAmount =
    item.type === "airtime" ||
    item.type === "electric" ||
    (item.price_type === "percent" && item.type !== "topup");

  return {
    type: item.type,
    plan_id: Number(item.planId),
    quantity: item.quantity,
    unique_number: item.type === "dg_coins" ? undefined : item.unique_number,
    amount: isAmount && extra ? Number(extra) : undefined,
    server: item.type === "topup" ? extra : undefined,
  };
};

// Buys the whole cart with one payment; the backend delivers each unit as its
// own purchase, one after the other.
export const checkoutCart = createAsyncThunk<
  CartCheckoutResponse,
  CartCheckoutRequest,
  { rejectValue: { message: string }; state: RootState }
>("cart/checkout", async ({ userId, ...payload }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/cart-checkout`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return res.data as CartCheckoutResponse;
  } catch (err: any) {
    const message =
      err?.response?.data?.message || err?.message || "Checkout failed";
    return thunkAPI.rejectWithValue({ message });
  }
});

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart: (
      state,
      action: PayloadAction<{ userId: string; item: Omit<CartItem, "id"> }>,
    ) => {
      const { userId, item } = action.payload;
      if (!state.userCarts[userId]) {
        state.userCarts[userId] = [];
      }

      const itemId = generateCartItemId(item);
      const existingIndex = state.userCarts[userId].findIndex(
        (i) => i.id === itemId,
      );

      if (existingIndex > -1) {
        state.userCarts[userId][existingIndex].quantity += item.quantity;
      } else {
        state.userCarts[userId].push({ ...item, id: itemId });
      }
    },

    incrementQuantity: (
      state,
      action: PayloadAction<{ userId: string; itemId: string }>,
    ) => {
      const { userId, itemId } = action.payload;
      const item = state.userCarts[userId]?.find((i) => i.id === itemId);
      if (item) {
        item.quantity += 1;
      }
    },

    decrementQuantity: (
      state,
      action: PayloadAction<{ userId: string; itemId: string }>,
    ) => {
      const { userId, itemId } = action.payload;
      const item = state.userCarts[userId]?.find((i) => i.id === itemId);
      if (item) {
        if (item.quantity > 1) {
          item.quantity -= 1;
        } else {
          state.userCarts[userId] = state.userCarts[userId].filter(
            (i) => i.id !== itemId,
          );
        }
      }
    },

    removeFromCart: (
      state,
      action: PayloadAction<{ userId: string; itemId: string }>,
    ) => {
      const { userId, itemId } = action.payload;
      if (state.userCarts[userId]) {
        state.userCarts[userId] = state.userCarts[userId].filter(
          (i) => i.id !== itemId,
        );
      }
    },

    updateQuantity: (
      state,
      action: PayloadAction<{
        userId: string;
        itemId: string;
        quantity: number;
      }>,
    ) => {
      const { userId, itemId, quantity } = action.payload;
      if (state.userCarts[userId]) {
        const item = state.userCarts[userId].find((i) => i.id === itemId);
        if (item) {
          item.quantity = Math.max(1, quantity);
        }
      }
    },

    clearCart: (state, action: PayloadAction<{ userId: string }>) => {
      const { userId } = action.payload;
      if (state.userCarts[userId]) {
        state.userCarts[userId] = [];
      }
    },
  },
});

export const {
  addToCart,
  incrementQuantity,
  decrementQuantity,
  removeFromCart,
  updateQuantity,
  clearCart,
} = cartSlice.actions;

export const selectUserCartItems = (
  state: RootState,
  userId: string,
): CartItem[] => state.cart?.userCarts?.[userId] || [];

const getUserSummary = (
  items: CartItem[],
  targetCurrency?: any,
  currencies: any[] = [],
  dgCoinCurrency?: any,
  dgPointCurrency?: any,
) => {
  const safeCurrencies = Array.isArray(currencies) ? currencies : [];

  const getCurrencyById = (currencyId?: number) =>
    safeCurrencies.find((c: any) => c.id === currencyId) ||
    targetCurrency ||
    safeCurrencies[0];

  return items.reduce(
    (acc, item) => {
      const qty = item.quantity || 1;
      const fromCurrency = getCurrencyById(item.priceCurrencyId);
      const toCurrency = targetCurrency || fromCurrency;

      const unitPrice = Number(item.price) || 0;
      const unitOriginalPrice = Number(item.originalPrice) || unitPrice;

      const baseUnitPrice =
        unitOriginalPrice > unitPrice ? unitOriginalPrice : unitPrice;

      const rawBasePrice = baseUnitPrice * qty;

      const rawLineDiscount =
        item.discount !== undefined && item.discount > 0
          ? item.discount * qty
          : item.discountPercentage
            ? (rawBasePrice * item.discountPercentage) / 100
            : 0;

      const rawBonusPoints = (item.bonusPoints || 0) * qty;

      const convertedSubtotal = convertCurrencyAmount(
        rawBasePrice,
        fromCurrency,
        toCurrency,
      );

      const convertedDiscount = convertCurrencyAmount(
        rawLineDiscount,
        fromCurrency,
        toCurrency,
      );

      const convertedBonusMonetaryValue = convertCurrencyAmount(
        rawBonusPoints,
        fromCurrency,
        toCurrency,
      );

      const itemCoins = calculateDGCoinAmount(
        item.price,
        item.priceCurrencyId,
        safeCurrencies,
        dgCoinCurrency,
      );
      const itemPoints = calculateDGCoinAmount(
        item.price,
        item.priceCurrencyId,
        safeCurrencies,
        dgPointCurrency,
      );

      acc.subtotal = Number((acc.subtotal + convertedSubtotal).toFixed(2));
      acc.totalDiscount = Number(
        (acc.totalDiscount + convertedDiscount).toFixed(2),
      );
      acc.totalBonusMonetaryValue = Number(
        (acc.totalBonusMonetaryValue + convertedBonusMonetaryValue).toFixed(2),
      );

      acc.totalCoins += Number(itemCoins || 0) * qty;
      acc.totalPoints += Number(itemPoints || 0) * qty;
      acc.totalBonusPoints += rawBonusPoints;
      acc.totalQuantity += qty;

      return acc;
    },
    {
      subtotal: 0,
      totalDiscount: 0,
      totalCoins: 0,
      totalPoints: 0,
      totalBonusPoints: 0,
      totalBonusMonetaryValue: 0,
      totalQuantity: 0,
      get grandTotal() {
        return Math.max(
          0,
          Number((this.subtotal - this.totalDiscount).toFixed(2)),
        );
      },
      get effectiveDiscountPercent() {
        return this.subtotal > 0
          ? Math.round((this.totalDiscount / this.subtotal) * 100)
          : 0;
      },
    },
  );
};

export const selectUserCartSummary = (
  state: RootState,
  userId: string,
  targetCurrency?: any,
  currencies: any[] = [],
  dgCoinCurrency?: any,
  dgPointCurrency?: any,
) => {
  const rawItems = state.cart?.userCarts?.[userId] || [];
  return getUserSummary(
    rawItems,
    targetCurrency,
    currencies,
    dgCoinCurrency,
    dgPointCurrency,
  );
};

export const selectUserCartSummaryAtm = (
  state: RootState,
  userId: string,
  targetCurrency?: any,
  currencies: any[] = [],
  dgCoinCurrency?: any,
  dgPointCurrency?: any,
) => {
  const rawItems = state.cart?.userCarts?.[userId] || [];
  const items = getAtmItems(rawItems);
  return {
    items,
    ...getUserSummary(
      items,
      targetCurrency,
      currencies,
      dgCoinCurrency,
      dgPointCurrency,
    ),
  };
};

const getAtmItems = (cartItems: CartItem[]) => {
  return cartItems.map((item) => {
    const {
      discountPercent,
      isFixed,
      rawBonusAmount,
      rawOriginalPrice,
      rawUnitDiscount,
      rawWalletPrice,
    } = computeCartItemData(item, true, item.type, item.additionalInputValue);
    return {
      ...item,
      price: rawWalletPrice,
      originalPrice: rawOriginalPrice,
      discount: rawUnitDiscount,
      discountPercentage: discountPercent,
      bonusPoints: Math.round(rawBonusAmount),
      isFixed,
    };
  });
};

export default cartSlice.reducer;
